import { useDebounce } from '@uidotdev/usehooks';
import { useState } from 'react';
import { GoPlus } from 'react-icons/go';
import { RxCross2 } from 'react-icons/rx';
import type { AnnuaireSanteProfessional } from '@aldebaran/common/types/structuredValue';
import { formatStructuredValue } from '@aldebaran/common/util/structuredValue';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  ANNUAIRE_SANTE_MIN_QUERY_CHARS,
  useAnnuaireSanteSearch,
} from '../hooks/useAnnuaireSanteSearch';
import { Value, ViewableResponse } from '../types/global';
import { AutocompleteDialog } from './autocomplete/AutocompleteDialog';

type ResponseAnnuaireSanteFieldProps = {
  response: ViewableResponse;
  value: AnnuaireSanteProfessional | null;
  /** Receives the picked professional, or '' to clear the answer. */
  onChange: (value: Value) => void;
  questionLabel?: string | null;
  questionComment?: string;
  disabled?: boolean;
};

type ManualDraft = { name: string; city: string };

/**
 * ANNUAIRE_SANTE_AUTOCOMPLETE: pick a doctor from the national directory through
 * the same modal shell as the KB autocomplete. Candidates, the pill and the report
 * all read the same `formatStructuredValue` text. The manual path keeps a required
 * question from dead-ending when the directory has no match.
 */
export const ResponseAnnuaireSanteField = ({
  response,
  value,
  onChange,
  questionLabel,
  questionComment,
  disabled,
}: ResponseAnnuaireSanteFieldProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  // null = search mode; a draft = manual entry mode
  const [manual, setManual] = useState<ManualDraft | null>(null);
  const debouncedSearch = useDebounce(searchText, 300);
  const {
    data: results,
    isFetching,
    isError,
  } = useAnnuaireSanteSearch(debouncedSearch);

  const close = () => {
    setIsOpen(false);
    setSearchText('');
    setManual(null);
  };
  const select = (professional: AnnuaireSanteProfessional) => {
    onChange(professional);
    close();
  };
  const submitManual = () => {
    if (!manual?.name.trim()) return;
    select({
      kind: 'ANNUAIRE_SANTE_PROFESSIONAL',
      origin: 'manual',
      rpps: null,
      title: null,
      firstName: null,
      lastName: manual.name.trim(),
      specialty: null,
      city: manual.city.trim() || null,
      postalCode: null,
      siteId: null,
    });
  };

  const typedEnough =
    searchText.trim().length >= ANNUAIRE_SANTE_MIN_QUERY_CHARS;
  // Until the debounce catches up nothing has been requested yet, so the previous
  // query's rows and "Aucun médecin trouvé" must not show for the new text.
  const isPending = searchText.trim() !== debouncedSearch.trim();
  const resultsHeading = isPending
    ? 'Recherche…'
    : isError
    ? 'La recherche est indisponible pour le moment'
    : isFetching && !results
    ? 'Recherche…'
    : results?.length
    ? 'Suggestions'
    : 'Aucun médecin trouvé';

  return (
    <>
      <div className="flex flex-col items-start justify-center w-full">
        {value ? (
          <div
            className="rounded-lg flex flex-row bg-blue-200 items-center pl-4 text-base w-full justify-between h-12"
            onClick={() => !disabled && setIsOpen(true)}
          >
            <p className="mr-2 truncate font-semibold">
              {formatStructuredValue(value)}
            </p>
            <button
              type="button"
              aria-label="Retirer"
              disabled={disabled}
              className="pr-4 py-3 pl-1"
              onClick={(e) => {
                e.stopPropagation();
                onChange('');
              }}
            >
              <RxCross2 size={22} strokeWidth={0.3} />
            </button>
          </div>
        ) : (
          <Button
            variant="autocomplete"
            size="autocomplete"
            disabled={disabled}
            onClick={() => setIsOpen(true)}
          >
            <span>{response.label}</span>
            <GoPlus strokeWidth={0.2} />
          </Button>
        )}
      </div>

      <AutocompleteDialog
        open={isOpen}
        onClose={close}
        questionLabel={questionLabel ?? undefined}
        questionDescription={questionComment}
        searchText={searchText}
        onSearchTextChange={setSearchText}
        searchPlaceholder="Nom du médecin"
      >
        {manual ? (
          <div className="flex flex-col gap-4 px-4 py-6">
            {/* Stored as typed: the keyboard must not rewrite a proper noun, and
                the browser must not offer the patient's own name or town for
                the doctor's. */}
            <Input
              autoCorrect="off"
              spellCheck={false}
              autoComplete="off"
              value={manual.name}
              placeholder="Nom et prénom du médecin"
              onChange={(e) => setManual({ ...manual, name: e.target.value })}
            />
            <Input
              autoCorrect="off"
              spellCheck={false}
              autoComplete="off"
              value={manual.city}
              placeholder="Ville (facultatif)"
              onChange={(e) => setManual({ ...manual, city: e.target.value })}
            />
            <Button
              variant="ctaPrimary"
              size="xs"
              disabled={!manual.name.trim()}
              onClick={submitManual}
            >
              Valider
            </Button>
            <button
              type="button"
              className="text-sm text-blue-500 underline text-left"
              onClick={() => setManual(null)}
            >
              Revenir à la recherche
            </button>
          </div>
        ) : (
          <>
            {typedEnough && (
              <>
                <div className="py-6">
                  <span className="font-inter text-sm text-[#B0B0B0]">
                    {resultsHeading}
                  </span>
                </div>
                <div className="flex flex-col gap-6 px-4 pb-4">
                  {!isPending &&
                    results?.map((professional) => (
                      <div
                        key={`${professional.rpps}-${professional.city ?? ''}`}
                        className="cursor-pointer"
                        onClick={() => select(professional)}
                      >
                        {formatStructuredValue(professional)}
                      </div>
                    ))}
                </div>
              </>
            )}
            {/* Always reachable, even under 3 chars or with the API down:
                a required question never dead-ends. */}
            <button
              type="button"
              className="px-4 py-6 text-sm text-blue-500 underline text-left"
              onClick={() => setManual({ name: searchText.trim(), city: '' })}
            >
              Je ne trouve pas mon médecin
            </button>
          </>
        )}
      </AutocompleteDialog>
    </>
  );
};
